import { Component, computed, effect, inject } from '@angular/core';
import { AttrPath, Table, UitstroomtypeVSO, VergelijkingUitstroomniveauVSO } from '@cumlaude/metadata';
import { generateCssClassForString } from '@cumlaude/shared-utils';
import { camelCase, groupBy, last, range } from 'lodash-es';
import { Observable } from 'rxjs';
import { DashboardHeaderComponent } from '../../dashboard-header/dashboard-header.component';
import { FilterBarComponent } from '../../filter-bar/filter-bar.component';
import { FilterPanelComponent } from '../../filter-panel/filter-panel.component';
import { DashboardContainerComponent } from '../../layout/dashboard-container/dashboard-container.component';
import { countRecords, SingleAggregator } from '../../services/aggregation';
import { Level, Path } from '../../services/data-tree';
import {
	BasicFilterExpression,
	DataOptions,
	DataResponse,
	DataService,
	defaultDoorstroomAvailableGroups,
	ExportFilter,
} from '../../services/data.service';
import { att, percOfRow } from '../../services/measures';
import { BarInfo } from '../../services/stacked-bars';
import { DashboardVariant } from '../../services/weergave-opties';
import { ColumnDef, TableModel } from '../../shared/components/table/table/table.model';
import { BarchartTableConfig } from '../../shared/dashboard/barchart-table/barchart-table-config';
import { BarchartTableComponent } from '../../shared/dashboard/barchart-table/barchart-table.component';
import { Attributes, BaseDashboardConfig, getLevelFilters } from '../../shared/dashboard/base-dashboard/base-dashboard-config';
import { DashboardContext } from '../../shared/dashboard/base-dashboard/dashboard-context';
import { createMeasureColumn, DataRow } from '../../shared/dashboard/data-tree-table/data-tree-table';
import { PartitionMeasure, VbarchartTableComponent } from '../../shared/dashboard/vbarchart-table/vbarchart-table.component';
import { WeergaveOptieComponent } from '../../shared/components/weergave-optie/weergave-optie.component';
import { FilterName } from '../../services/filter-name';
import { LegendaComponent } from '../../legenda/legenda.component';
import { ToggleButtonsComponent } from '@cumlaude/shared-components-buttons';

export interface BehaaldNiveauConformOPPI extends Attributes {
	ds_fun_nm_vergelijk_einduitstroomniveau_vso: VergelijkingUitstroomniveauVSO;
}

export interface BehaaldNiveauConformOPPA extends Attributes {
	opNiveau: number;
	bovenNiveau: number;
	onderNiveau: number;
	onbekend: number;
	bekend: number;
}

@Component({
	selector: 'app-behaald-niveau-conform-opp',
	standalone: true,
	templateUrl: './behaald-niveau-conform-opp.component.html',
	styleUrls: [],
	imports: [
		DashboardContainerComponent,
		FilterPanelComponent,
		DashboardHeaderComponent,
		BarchartTableComponent,
		FilterBarComponent,
		VbarchartTableComponent,
		WeergaveOptieComponent,
		LegendaComponent,
		ToggleButtonsComponent,
	],
})
export class BehaaldNiveauConformOPPComponent extends BarchartTableConfig<BehaaldNiveauConformOPPI, BehaaldNiveauConformOPPA> {
	private readonly dataService = inject(DataService);

	override availableGroups: AttrPath[] = [
		...defaultDoorstroomAvailableGroups,
		['ds_fk_lb_van', 'lb_nm_uitstroomniveau_vso'],
		['ds_fk_lb_van', 'lb_nm_beoogd_uitstroomniveau_vso'],
		['ds_fk_ll', 'll_des_uitschrijving'],
	];

	readonly historieGroups = computed(() => this.groeperingService.huidigeGroeperingen().slice(0, -1));
	readonly historieSubgroups = computed(() => {
		return [...this.groeperingService.huidigeGroeperingen().slice(-1), ['ds_nm_schooljaar_van'] as AttrPath, ...this.subgroups];
	});

	readonly subgroups: AttrPath[] = [['ds_fun_nm_vergelijk_einduitstroomniveau_vso']];

	readonly actueelFilters: FilterName[] = [
		'x_prestatieanalyse_ds_schooljaar',
		'ds_fk_br_vest_van.br_co_brin',
		'ds_fk_vs_van.vs_nm_vestiging',
		'ds_fk_ilt_van.ilt_nm_niveau',
		'ds_nr_leerjaar_van',
		'ds_nm_prestatieanalyse_vso_uitzondering',
		'ds_fk_ll.ll_des_uitschrijving',
	];

	readonly historieFilters: FilterName[] = [
		'x_doorstroom_schooljaar_historie', //
		'x_doorstroom_multiselect_schooljaar',
		...this.actueelFilters.slice(1),
	];

	readonly permanentFilterExpressions = [
		// Bij VSO speelt 1 oktober geen rol, leerlingen stromen het hele jaar in en uit. We laten dus gegevens zien van de plaatsing waarin de leerling daadwerkelijk uitstroomt.
		new BasicFilterExpression(['ds_is_plaatsing_opeenvolgend'], 1),
		new BasicFilterExpression(['ds_nm_uitstroomtype_vso'], UitstroomtypeVSO.EINDUITSTROOM),
		new BasicFilterExpression(['ds_fk_vs_van', 'vs_is_vso'], 1),
	];

	readonly factTable = Table.fac_ds_doorstroom;

	getData(options: DataOptions): Observable<DataResponse<number[]>> {
		return this.dataService.getDoorstroomData({
			...options,
			r: range(0, this.groeperingService.huidigeAantalGroeperingen() + 1),
		});
	}

	variant = this.qp.signal('variant');

	vergelijkingUitstroomNiveauVso = this.qp.signal('uitstroomniveauvso');

	constructor() {
		super();
		effect(() => {
			this.vergelijkingUitstroomNiveauVso();
			this.filterService.refresh();
		});
	}

	override isHistorieBatchVariant(): boolean {
		return this.variant() === DashboardVariant.HISTORIE && this.groeperingService.huidigeAantalGroeperingen() > 0;
	}

	protected override singleAggregators: Partial<{
		[ai in keyof BehaaldNiveauConformOPPA]: SingleAggregator<BehaaldNiveauConformOPPI, BehaaldNiveauConformOPPA[ai]>;
	}> = {
		opNiveau: countRecords(
			(attrs: BehaaldNiveauConformOPPI) => attrs.ds_fun_nm_vergelijk_einduitstroomniveau_vso === VergelijkingUitstroomniveauVSO.OP_NIVEAU
		),

		bovenNiveau: countRecords(
			(attrs: BehaaldNiveauConformOPPI) => attrs.ds_fun_nm_vergelijk_einduitstroomniveau_vso === VergelijkingUitstroomniveauVSO.BOVEN_NIVEAU
		),
		onderNiveau: countRecords(
			(attrs: BehaaldNiveauConformOPPI) => attrs.ds_fun_nm_vergelijk_einduitstroomniveau_vso === VergelijkingUitstroomniveauVSO.ONDER_NIVEAU
		),
		onbekend: countRecords((attrs: BehaaldNiveauConformOPPI) => attrs.ds_fun_nm_vergelijk_einduitstroomniveau_vso === null),
		bekend: countRecords((attrs: BehaaldNiveauConformOPPI) => attrs.ds_fun_nm_vergelijk_einduitstroomniveau_vso !== null),
	};

	override partitionBarData(
		rowRoot: Level<BehaaldNiveauConformOPPA, number[]>,
		_context: DashboardContext<
			BehaaldNiveauConformOPPI,
			BehaaldNiveauConformOPPA,
			BaseDashboardConfig<BehaaldNiveauConformOPPI, BehaaldNiveauConformOPPA>
		>
	): Path<BehaaldNiveauConformOPPA, number[]>[][] {
		const { opNiveau, onderNiveau, bovenNiveau, onbekend } = groupBy(rowRoot.r, (r) => camelCase(last(r)?.k ?? 'Onbekend'));
		return [opNiveau, [...(bovenNiveau ?? []), ...(onderNiveau ?? [])], onbekend].filter((records) => records?.length);
	}

	override makeBar(
		attrs: BehaaldNiveauConformOPPI,
		path: Path<BehaaldNiveauConformOPPA, number[]>,
		context: DashboardContext<
			BehaaldNiveauConformOPPI,
			BehaaldNiveauConformOPPA,
			BaseDashboardConfig<BehaaldNiveauConformOPPI, BehaaldNiveauConformOPPA>
		>
	): BarInfo {
		const vergelijking = this.displayService.display(attrs.ds_fun_nm_vergelijk_einduitstroomniveau_vso, [
			'ds_fun_nm_vergelijk_einduitstroomniveau_vso',
		]);
		return {
			...super.makeBar(attrs, path, context),
			text: this.variant() === DashboardVariant.ACTUEEL ? vergelijking : undefined,
			tooltip: `${vergelijking}: ${attrs.count_records}`,
			className: generateCssClassForString(vergelijking),
		};
	}

	override createMeasureColumns(
		context: DashboardContext<BehaaldNiveauConformOPPI, BehaaldNiveauConformOPPA, BehaaldNiveauConformOPPComponent>
	): ColumnDef<DataRow<BehaaldNiveauConformOPPA>>[] {
		if (this.variant() === DashboardVariant.HISTORIE) return [];

		const columns = [];
		switch (this.vergelijkingUitstroomNiveauVso()) {
			case VergelijkingUitstroomniveauVSO.ONDER_NIVEAU:
				columns.push(
					this.createLeerlingColumn('% onder niveau', percOfRow('onderNiveau', 'bekend'), { context, dataType: 'percentage' }, undefined, [
						new BasicFilterExpression(['ds_fun_nm_vergelijk_einduitstroomniveau_vso'], VergelijkingUitstroomniveauVSO.ONDER_NIVEAU),
					]),
					this.createLeerlingColumn('Onder niveau', att('onderNiveau'), { context }, undefined, [
						new BasicFilterExpression(['ds_fun_nm_vergelijk_einduitstroomniveau_vso'], VergelijkingUitstroomniveauVSO.ONDER_NIVEAU),
					])
				);
				break;
			case VergelijkingUitstroomniveauVSO.OP_NIVEAU:
				columns.push(
					this.createLeerlingColumn('% op niveau', percOfRow('opNiveau', 'bekend'), { context, dataType: 'percentage' }, undefined, [
						new BasicFilterExpression(['ds_fun_nm_vergelijk_einduitstroomniveau_vso'], VergelijkingUitstroomniveauVSO.OP_NIVEAU),
					]),
					this.createLeerlingColumn('Op niveau', att('opNiveau'), { context }, undefined, [
						new BasicFilterExpression(['ds_fun_nm_vergelijk_einduitstroomniveau_vso'], VergelijkingUitstroomniveauVSO.OP_NIVEAU),
					])
				);
				break;
			case VergelijkingUitstroomniveauVSO.BOVEN_NIVEAU:
				columns.push(
					this.createLeerlingColumn('% boven niveau', percOfRow('bovenNiveau', 'bekend'), { context, dataType: 'percentage' }, undefined, [
						new BasicFilterExpression(['ds_fun_nm_vergelijk_einduitstroomniveau_vso'], VergelijkingUitstroomniveauVSO.BOVEN_NIVEAU),
					]),
					this.createLeerlingColumn('Boven niveau', att('bovenNiveau'), { context }, undefined, [
						new BasicFilterExpression(['ds_fun_nm_vergelijk_einduitstroomniveau_vso'], VergelijkingUitstroomniveauVSO.BOVEN_NIVEAU),
					])
				);
				break;
		}

		return [
			...columns,
			this.createLeerlingColumn('Leerlingen', att('bekend'), { context }, undefined, [
				new BasicFilterExpression(['ds_fun_nm_vergelijk_einduitstroomniveau_vso'], null, '<>'),
			]),
			createMeasureColumn('Onbekend', att('onbekend'), { context, clickHandler: (path) => this.handleUitzonderingRedirect(path, context) }),
		];
	}

	protected handleUitzonderingRedirect(
		path: Path<BehaaldNiveauConformOPPA, number[]>,
		context: DashboardContext<BehaaldNiveauConformOPPI, BehaaldNiveauConformOPPA, BehaaldNiveauConformOPPComponent>
	) {
		const params = {
			from: this.urlService.getFrom(),
		};

		for (const levelFilter of getLevelFilters(context, path)) {
			Object.assign(params, this.filterService.encodeFilterValue(<FilterName>levelFilter.attr.join('.'), [levelFilter.val]));
		}

		this.urlService.redirectDoorklik([this.getUitzonderingUrl()], params);
	}

	getUitzonderingUrl(): string {
		return '/details/uitzondering/uitstroomniveau';
	}

	readonly partitionMeasure = computed<PartitionMeasure<BehaaldNiveauConformOPPA>>(() => {
		let attribute: keyof BehaaldNiveauConformOPPA;
		switch (this.vergelijkingUitstroomNiveauVso()) {
			case VergelijkingUitstroomniveauVSO.ONDER_NIVEAU:
				attribute = 'onderNiveau';
				break;
			case VergelijkingUitstroomniveauVSO.OP_NIVEAU:
				attribute = 'opNiveau';
				break;
			case VergelijkingUitstroomniveauVSO.BOVEN_NIVEAU:
				attribute = 'bovenNiveau';
				break;
		}
		return {
			type: 'percentage',
			getValue: percOfRow(attribute, 'bekend'),
		};
	});

	protected readonly DashboardVariant = DashboardVariant;

	override enrichTableModel(
		_context: DashboardContext<BehaaldNiveauConformOPPI, BehaaldNiveauConformOPPA, BehaaldNiveauConformOPPComponent>,
		tableModel: TableModel<DataRow<BehaaldNiveauConformOPPA>>
	) {
		tableModel.showFooters = this.variant() === DashboardVariant.ACTUEEL;
	}

	protected readonly VergelijkingUitstroomniveauVSO = VergelijkingUitstroomniveauVSO;

	override getDisplayOptions(): ExportFilter[] {
		return this.variant() === DashboardVariant.HISTORIE ? [{ label: 'Weergave', value: this.vergelijkingUitstroomNiveauVso() }] : [];
	}
}
